import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { addContract } from '../../../../redux/Slices/ContractSlice';
import { getQuotations } from '../../../../redux/Slices/authSlice';
import axios from 'axios';
import { BASE_URL, getToken, getCurrentLanguage } from '../../../../shared/utils.js/utils';


// normalize list response 
const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };
  
  let items = [];
  let pagination = null;
  
  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }
  
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }
  
  return { items, pagination };
};

const ContractFromQuotation = () => {
  const { quotationId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { quotations, quotationsPagination } = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    quotation_id: quotationId || '',
    date: '',
    start_date: '',
    end_date: '',
    retention_percent: '',
    advance_payment_percent: '',
    notes: '',
  });

  // Quotation state variable  
  const [quotationDropdownOpen, setQuotationDropdownOpen] = useState(false);
  const [quotationSearchTerm, setQuotationSearchTerm] = useState('');
  const [quotationOptions, setQuotationOptions] = useState([]);
  const [quotationPage, setQuotationPage] = useState(1);
  const [quotationPagination, setQuotationPagination] = useState(null);
  const quotationSearchInputRef = useRef(null);

  const fetchQuotations = useCallback(
    async (page = quotationPage, search = quotationSearchTerm) => {
      try {
        const token = getToken();
        if (!token) throw new Error(t('contracts.fromQuotation.messages.noAuthToken'));
        const currentLang = getCurrentLanguage();
        const response = await axios.get(`${BASE_URL}/quotations`, {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': currentLang,
          },
        });
        const { items, pagination: paginationMeta } = normalizeListResponse(response.data);
        setQuotationOptions(items);
        setQuotationPagination(paginationMeta);
      } catch (error) {
        toast.error(error?.response?.data?.message || t('contracts.fromQuotation.messages.fetchQuotationsError'), { rtl: isRTL });
      }
    },
    [quotationPage, quotationSearchTerm, t, isRTL]
  );

  useEffect(() => {
    if (quotationId) {
      setForm((prev) => ({ ...prev, quotation_id: quotationId }));
      fetchQuotations(1, '').then(() => {
      });
    }
  }, [quotationId, fetchQuotations]);

  useEffect(() => {
    if (quotationDropdownOpen) {
      fetchQuotations(quotationPage, quotationSearchTerm);
    }
  }, [quotationDropdownOpen, quotationPage, fetchQuotations]);

  useEffect(() => {
    if (quotationSearchTerm !== undefined && quotationDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setQuotationPage(1);
        fetchQuotations(1, quotationSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [quotationSearchTerm, quotationDropdownOpen, fetchQuotations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.quotation_id) {
      toast.error(t('contracts.fromQuotation.messages.selectQuotation'), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmitting(true);
      await dispatch(
        addContract({
          quotation_id: form.quotation_id,
          date: form.date,
          start_date: form.start_date,
          end_date: form.end_date,
          retention_percent: form.retention_percent ? Number(form.retention_percent) : undefined,
          advance_payment_percent: Number(form.advance_payment_percent || 0),
          notes: form.notes,
        })
      ).unwrap();
      toast.success(t('contracts.fromQuotation.messages.addSuccess'), { rtl: isRTL });
      navigate('/contracts');
    } catch (error) {
      toast.error(error || t('contracts.fromQuotation.messages.addError'), { rtl: isRTL });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedQuotation = () => {
    if (!form.quotation_id) return null;
    return quotationOptions.find((q) => String(q.id) === String(form.quotation_id));
  };

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <h2 style={{ marginBottom: 20, fontSize: '20px' }}>{t('contracts.fromQuotation.title')}</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('contracts.fromQuotation.quotation')}</span>
              <div onClick={() => setQuotationDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                <span>
                  {getSelectedQuotation()
                    ? `${getSelectedQuotation().quotation_no || `#${getSelectedQuotation().id}`}${getSelectedQuotation().subject ? ` - ${getSelectedQuotation().subject}` : ''}${getSelectedQuotation().project ? ` - ${isRTL ? (getSelectedQuotation().project.name_ar || getSelectedQuotation().project.name_en || '') : (getSelectedQuotation().project.name_en || getSelectedQuotation().project.name_ar || '')}` : ''}`
                    : t('contracts.fromQuotation.selectQuotation')}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              <DropdownWithSearch
                isOpen={quotationDropdownOpen}
                searchTerm={quotationSearchTerm}
                onSearchChange={setQuotationSearchTerm}
                searchInputRef={quotationSearchInputRef}
                items={quotationOptions}
                pagination={quotationPagination}
                onPageChange={setQuotationPage}
                onClose={() => setQuotationDropdownOpen(false)}
                onSelect={(item) => {
                  setForm((prev) => ({ ...prev, quotation_id: String(item.id) }));
                  setQuotationDropdownOpen(false);
                  setQuotationSearchTerm('');
                  setQuotationPage(1);
                }}
                label="عرض سعر"
              />
            </label>
          </div>

          <div style={gridStyle}>
            <Input label={t('contracts.fromQuotation.date')} type="date" name="date" value={form.date} onChange={handleChange} required isRTL={isRTL} />
            <Input
              label={t('contracts.fromQuotation.startDate')}
              type="date"
              name="start_date"
              value={form.start_date}
              onChange={handleChange}
              required
              isRTL={isRTL}
            />
            <Input label={t('contracts.fromQuotation.endDate')} type="date" name="end_date" value={form.end_date} onChange={handleChange} required isRTL={isRTL} />
          </div>
          <div style={gridStyle}>
            <Input
              label={t('contracts.fromQuotation.retentionPercent')}
              type="number"
              min="0"
              name="retention_percent"
              value={form.retention_percent}
              onChange={handleChange}
              isRTL={isRTL}
            />
            <Input
              label={t('contracts.fromQuotation.advancePaymentPercent')}
              type="number"
              min="0"
              name="advance_payment_percent"
              value={form.advance_payment_percent}
              onChange={handleChange}
              required
              isRTL={isRTL}
            />
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{t('contracts.fromQuotation.notes')}</span>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={4}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                resize: 'vertical',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
            <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
              {isSubmitting ? t('contracts.fromQuotation.saving') : t('contracts.fromQuotation.save')}
            </button>
            <button type="button" style={secondaryButtonStyle} onClick={() => navigate('/contracts')}>
              {t('contracts.fromQuotation.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Input = ({ label, isRTL = true, ...rest }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 14 }}>{label}</span>
    <input
      {...rest}
      style={{
        padding: 12,
        borderRadius: 8,
        border: '1px solid #333',
        backgroundColor: '#1a1f2e',
        color: 'white',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
      }}
    />
  </label>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 700,
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const submitButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const dropdownDisplayStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'right',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : (i18n?.language === 'ar');
  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={dropdownMenuStyle} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t('contracts.dropdown.searchFor')} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={dropdownSearchStyle}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11 }}>{t('contracts.dropdown.page')} {pagination.current_page} {t('contracts.dropdown.of')} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={dropdownEmptyStyle}>{t('contracts.dropdown.noResults')}</div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{ padding: '10px 12px', borderTop: '1px solid #333', cursor: 'pointer' }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatQuotation(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatQuotation = (quotation, language = 'ar') => {
  if (!quotation) return '';
  const quotationNo = quotation.quotation_no || (quotation.id ? `#${quotation.id}` : '');
  const subject = quotation.subject || '';
  const project = quotation.project ? ` - ${language === 'ar' ? (quotation.project.name_ar || quotation.project.name_en || '') : (quotation.project.name_en || quotation.project.name_ar || '')}` : '';
  return `${quotationNo}${subject ? ` - ${subject}` : ''}${project}` || `#${quotation.id}`;
};

export default ContractFromQuotation;

